import config from '@/config';

export async function updateDescription(
  type: 'stream' | 'workspace',
  id: number,
  description: string
): Promise<boolean> {
  try {
    const response = await fetch(`${config.API_BASE_URL}/api/v1/update/${type}s/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        [`${type}_id`]: id,
        [`${type}_description`]: description,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update ${type} description`);
    }

    return true;
  } catch (error) {
    console.error(`Error updating ${type} description:`, error);
    return false;
  }
}

export async function generateQuestions(
  streamId: number,
  workspaceId: number,
  headlineGroupId: number
): Promise<{ success: boolean; insightId?: number; message?: string }> {
  try {
    const response = await fetch(`${config.API_BASE_URL}/api/v1/generate/user_questions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        stream_id: streamId,
        workspace_id: workspaceId,
        headline_group_id: headlineGroupId
      }),
    });

    const data = await response.json();
    return {
      success: data.success,
      insightId: data.insight_id,
      message: data.message
    };
  } catch (error) {
    console.error('Error generating questions:', error);
    return {
      success: false,
      message: 'Failed to generate questions'
    };
  }
}

interface UpdateQuestionsRequest {
  insight_id: number;
  text_main: string;
}

export async function updateQuestions(insightId: number, questions: string[]): Promise<{ success: boolean; message?: string }> {
  try {
    const formattedQuestions = questions.map((q, i) => `${i + 1}. ${q}`).join('\n');
    
    const response = await fetch(`${config.API_BASE_URL}/api/v1/update/questions`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        insight_id: insightId,
        text_main: formattedQuestions
      } as UpdateQuestionsRequest),
    });

    if (!response.ok) {
      throw new Error('Failed to update questions');
    }

    const data = await response.json();
    return {
      success: true,
      message: 'Questions updated successfully'
    };
  } catch (error) {
    console.error('Error updating questions:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to update questions'
    };
  }
}

export async function generateQnA(
  streamId: number,
  workspaceId: number,
  headlineGroupIds: number[],
  questions: string[]
): Promise<{ success: boolean; insightId?: number; message?: string }> {
  try {
    const response = await fetch(`${config.API_BASE_URL}/api/v1/generate/qna`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        stream_id: streamId,
        workspace_id: workspaceId,
        headline_group_ids: headlineGroupIds,
        questions: questions
      }),
    });

    const data = await response.json();
    return {
      success: data.success,
      insightId: data.insight_id,
      message: data.message
    };
  } catch (error) {
    console.error('Error generating QnA:', error);
    return {
      success: false,
      message: 'Failed to generate QnA'
    };
  }
}

export async function updateInsight(
  insightId: number,
  textMain: string,
  title?: string,
  summary?: string
): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetch(`${config.API_BASE_URL}/api/v1/update/insights/${insightId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        insight_id: insightId,
        text_main: textMain,
        title: title,
        summary: summary
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update insight');
    }

    return {
      success: true,
      message: 'Insight updated successfully'
    };
  } catch (error) {
    console.error('Error updating insight:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to update insight'
    };
  }
} 